import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../assets/css/Styles1.css';
import './CRUD.css';
import './EmpleadosCRUD.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useNotification } from '../components/NotificationSystem';
import SEO from '../components/SEO';
import Icon from '../components/Icon';
import { pdf } from '@react-pdf/renderer';
import { MyDocument } from '../pages/Results';

function EmpleadosCRUD({ onLogout, userRole }) {
  const { success, error: showError } = useNotification();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [empleados, setEmpleados] = useState([]);
  const [empleadosFiltrados, setEmpleadosFiltrados] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [filtroArea, setFiltroArea] = useState('');
  const [filtroCargo, setFiltroCargo] = useState('');
  const [filtroRol, setFiltroRol] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('activo');
  const [filtroProyecto, setFiltroProyecto] = useState('');
  const [cargos, setCargos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const emptyEmpleado = {
    id_empleado: '',
    cedula: '',
    nombre: '',
    tipo_documento: 'Cédula de Ciudadanía',
    cargo: '',
    area: 'Administracion',
    fecha_inicio_contrato: '',
    email: '',
    contrasena: '',
    proyecto: '',
    ods: '',
    rol: 'empleado',
    estado: 'activo',
    tipo_poblacion: '',
    nivel_organizacional: '',
    config_evaluacion: '',
    jefe_inmediato: '',
    tiene_personal_a_cargo: 0,
    autoevaluacion: 'Sí',
    modalidad_evaluacion: '',
    ponderacion_aplicable: '',
    observaciones_gh: ''
  };

  const [currentEmpleado, setCurrentEmpleado] = useState({ ...emptyEmpleado });

  // URL base de la API
  const apiUrl = process.env.REACT_APP_API_BASE_URL || '';

  const [pdfGenerandoId, setPdfGenerandoId] = useState(null);

  // Mismo PDF que descarga el empleado en /results, para su evaluación más reciente
  const descargarPdfEmpleado = async (empleado) => {
    const employeeId = empleado.id_empleado;
    try {
      setPdfGenerandoId(employeeId);
      const listResp = await fetch(`${apiUrl}/api/evaluations/employee/${employeeId}`);
      if (!listResp.ok) throw new Error('No se pudo obtener el historial de evaluaciones');
      const listData = await listResp.json();
      const evaluaciones = listData.success && Array.isArray(listData.evaluaciones) ? listData.evaluaciones : [];
      if (evaluaciones.length === 0) {
        showError('Sin evaluaciones', `${empleado.nombre} no tiene evaluaciones registradas.`);
        return;
      }
      const evaluacion = evaluaciones[0];

      const response = await fetch(`${apiUrl}/api/evaluations/${evaluacion.id_evaluacion}/complete/${employeeId}`);
      if (!response.ok) throw new Error('Error al obtener datos completos de la evaluación');
      const { data: evaluationData } = await response.json();

      if (evaluationData.firmas && !Array.isArray(evaluationData.firmas)) {
        if (evaluationData.firmas.firma_empleado && !evaluationData.firmas.firma_empleado.startsWith('data:image')) {
          evaluationData.firmas.firma_empleado = `data:image/png;base64,${evaluationData.firmas.firma_empleado}`;
        }
        if (evaluationData.firmas.firma_jefe && !evaluationData.firmas.firma_jefe.startsWith('data:image')) {
          evaluationData.firmas.firma_jefe = `data:image/png;base64,${evaluationData.firmas.firma_jefe}`;
        }
      } else {
        evaluationData.firmas = { firma_empleado: null, firma_jefe: null };
      }

      const blob = await pdf(<MyDocument evaluationData={evaluationData} />).toBlob();
      const fileName = `evaluacion_${evaluacion.id_evaluacion}_${new Date().toISOString().split('T')[0]}.pdf`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      success('PDF generado', `Archivo PDF generado exitosamente: ${fileName}`);
    } catch (e) {
      showError('Error al generar PDF', e?.message || 'Error al generar el PDF. Intente nuevamente.');
    } finally {
      setPdfGenerandoId(null);
    }
  };

  // Cargar datos al montar el componente
  useEffect(() => {
    fetchEmpleados();
    fetchCargos();
  }, []);

  // Función para obtener todos los empleados
  const fetchEmpleados = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiUrl}/api/employees`);
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        setEmpleados(result.data);
        setEmpleadosFiltrados(result.data);
      } else {
        throw new Error(result.message || 'Error al cargar empleados');
      }
    } catch (error) {
      setError(error.message || 'Error al cargar empleados');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar por texto + área, cargo, rol y proyecto
  useEffect(() => {
    const q = (busqueda || '').toString().toLowerCase().trim();
    const filtrados = (empleados || []).filter(e => {
      if (filtroArea && (e.area || '') !== filtroArea) return false;
      if (filtroCargo && (e.cargo || '') !== filtroCargo) return false;
      if (filtroRol && (e.rol || '') !== filtroRol) return false;
      if (filtroProyecto && (e.proyecto || '') !== filtroProyecto) return false;
      if (filtroEstado && (e.estado || 'activo') !== filtroEstado) return false;

      if (!q) return true;

      const id = (e.id_empleado ?? '').toString().toLowerCase();
      const cedula = (e.cedula ?? '').toString().toLowerCase();
      const nombre = (e.nombre ?? '').toString().toLowerCase();
      const email = (e.email ?? '').toString().toLowerCase();
      const cargo = (e.cargo ?? '').toString().toLowerCase();
      const area = (e.area ?? '').toString().toLowerCase();
      const proyecto = (e.proyecto ?? '').toString().toLowerCase();
      const ods = (e.ods ?? '').toString().toLowerCase();
      const rol = (e.rol ?? '').toString().toLowerCase();

      return id.includes(q) || cedula.includes(q) || nombre.includes(q) ||
             email.includes(q) || cargo.includes(q) || area.includes(q) ||
             proyecto.includes(q) || ods.includes(q) || rol.includes(q);
    });
    setEmpleadosFiltrados(filtrados);
  }, [busqueda, filtroArea, filtroCargo, filtroRol, filtroProyecto, filtroEstado, empleados]);

  const areasDisponibles = Array.from(
    new Set((empleados || []).map(e => e.area).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b, 'es'));

  const cargosDisponibles = Array.from(
    new Set([
      ...(cargos || []).map(c => c.nombre_cargo).filter(Boolean),
      ...(empleados || []).map(e => e.cargo).filter(Boolean)
    ])
  ).sort((a, b) => a.localeCompare(b, 'es'));

  const proyectosDisponibles = Array.from(
    new Set((empleados || []).map(e => e.proyecto).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b, 'es'));

  const areasFormulario = Array.from(
    new Set([
      'Administracion',
      'Gestión de Proyectos',
      ...(empleados || []).map(e => e.area).filter(Boolean)
    ])
  ).sort((a, b) => a.localeCompare(b, 'es'));

  const jefesDisponibles = Array.from(
    new Set((empleados || []).map(e => e.nombre).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b, 'es'));

  const hayFiltrosActivos = Boolean(
    busqueda || filtroArea || filtroCargo || filtroRol || filtroProyecto || filtroEstado !== 'activo'
  );

  const limpiarFiltros = () => {
    setBusqueda('');
    setFiltroArea('');
    setFiltroCargo('');
    setFiltroRol('');
    setFiltroProyecto('');
    setFiltroEstado('activo');
  };

  // Función para obtener todos los cargos
  const fetchCargos = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/employees/cargos`);
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        setCargos(result.data);
      } else {
        throw new Error(result.message || 'Error al cargar cargos');
      }
    } catch (error) {
      // Silenciado
      // No mostramos error aquí para no interferir con la carga de empleados
    }
  };

  const handleBackClick = () => {
    navigate('/admin');
  };

  const handleAddClick = () => {
    setIsEditing(false);
    setCurrentEmpleado({ ...emptyEmpleado });
    setShowForm(true);
  };

  const handleEditClick = (empleado) => {
    setIsEditing(true);
    setCurrentEmpleado({
      ...emptyEmpleado,
      ...empleado,
      contrasena: '',
      tiene_personal_a_cargo: Number(empleado.tiene_personal_a_cargo) ? 1 : 0,
      fecha_inicio_contrato: empleado.fecha_inicio_contrato
        ? String(empleado.fecha_inicio_contrato).slice(0, 10)
        : ''
    });
    setShowForm(true);
  };

  const handleDeleteClick = async (id) => {
    if (window.confirm('¿Está seguro que desea eliminar este empleado?')) {
      try {
        const response = await fetch(`${apiUrl}/api/employees/${id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`Error: ${response.status} ${response.statusText}`);
        }
        
        const result = await response.json();
        
        if (result.success) {
          // Actualizar el estado eliminando el empleado
          setEmpleados(empleados.filter(emp => emp.id_empleado !== id));
          success('Empleado eliminado', 'Empleado eliminado con éxito');
        } else {
          throw new Error(result.message || 'Error al eliminar empleado');
        }
      } catch (error) {
        showError('Error al eliminar', `Error al eliminar empleado: ${error.message}`);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentEmpleado({
      ...currentEmpleado,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const isCreateOperation = !isEditing;
      const url = isCreateOperation 
        ? `${apiUrl}/api/employees` 
        : `${apiUrl}/api/employees/${currentEmpleado.id_empleado}`;
      
      const method = isCreateOperation ? 'POST' : 'PUT';
      const payload = { ...currentEmpleado };
      if (!isCreateOperation && !payload.contrasena) {
        delete payload.contrasena;
      }
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        if (isCreateOperation) {
          // Obtener el empleado recién creado para añadirlo al estado
          const getResponse = await fetch(`${apiUrl}/api/employees/${result.id}`);
          if (getResponse.ok) {
            const getData = await getResponse.json();
            if (getData.success) {
              setEmpleados([getData.data, ...empleados]);
            }
          } else {
            // Si no podemos obtener el empleado nuevo, recargamos todos
            fetchEmpleados();
          }
        } else {
          // Actualizar el empleado en el estado
          setEmpleados(empleados.map(emp => 
            emp.id_empleado === currentEmpleado.id_empleado ? currentEmpleado : emp
          ));
        }
        
        setShowForm(false);
        success(
          isCreateOperation ? 'Empleado creado' : 'Empleado actualizado', 
          isCreateOperation ? 'Empleado creado con éxito' : 'Empleado actualizado con éxito'
        );
      } else {
        throw new Error(result.message || `Error al ${isCreateOperation ? 'crear' : 'actualizar'} empleado`);
      }
    } catch (error) {
      showError('Error de operación', `Error: ${error.message}`);
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="crud-container empleados-crud">
        <Header onLogout={onLogout} userRole={userRole} />
        <main className="crud-main">
          <div className="loading">Cargando empleados...</div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="crud-container empleados-crud">
        <Header onLogout={onLogout} userRole={userRole} />
        <main className="crud-main">
          <div className="error">Error: {error}</div>
          <button onClick={fetchEmpleados}>Reintentar</button>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Gestión de Empleados - Panel de Administración"
        description="Gestiona la información de empleados en el sistema de evaluación de desempeño de Meridian Consulting LTDA. Agrega, edita y administra los datos del personal."
        keywords="gestión empleados, administración personal, CRUD empleados, Meridian Consulting, evaluación desempeño, administración sistema"
        url="https://evaluacion.meridianltda.com/admin/empleados"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Gestión de Empleados - Panel de Administración",
          "description": "Página de gestión de empleados del sistema de evaluación de desempeño",
          "url": "https://evaluacion.meridianltda.com/admin/empleados",
          "isPartOf": {
            "@type": "WebSite",
            "name": "Sistema de Evaluación de Desempeño - Meridian Consulting LTDA",
            "url": "https://evaluacion.meridianltda.com"
          },
          "about": {
            "@type": "Thing",
            "name": "Gestión de Empleados"
          }
        }}
      />
      <div className="crud-container empleados-crud">
      <Header onLogout={onLogout} userRole={userRole} />
      
      <main className="crud-main">
        <div className="crud-header">
          <button className="back-button" onClick={handleBackClick}>
            Volver al Panel
          </button>
          <div className="crud-header-titles">
            <h1>Gestión de Empleados</h1>
            <p className="crud-header-subtitle">Administra el personal del sistema de evaluación</p>
          </div>
          <button className="add-button" onClick={handleAddClick}>
            Agregar Empleado
          </button>
        </div>

        {/* Búsqueda y filtros */}
        <div className="crud-filters">
          <div className="crud-search">
            <input
              type="text"
              placeholder="Buscar por cédula, nombre, correo u ODS..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>

          <div className="crud-filters-row">
            <div className="filter-group">
              <label htmlFor="filtro-area">Área</label>
              <select
                id="filtro-area"
                value={filtroArea}
                onChange={(e) => setFiltroArea(e.target.value)}
              >
                <option value="">Todas</option>
                {areasDisponibles.map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="filtro-cargo">Cargo</label>
              <select
                id="filtro-cargo"
                value={filtroCargo}
                onChange={(e) => setFiltroCargo(e.target.value)}
              >
                <option value="">Todos</option>
                {cargosDisponibles.map(cargo => (
                  <option key={cargo} value={cargo}>{cargo}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="filtro-proyecto">Proyecto</label>
              <select
                id="filtro-proyecto"
                value={filtroProyecto}
                onChange={(e) => setFiltroProyecto(e.target.value)}
              >
                <option value="">Todos</option>
                {proyectosDisponibles.map(proyecto => (
                  <option key={proyecto} value={proyecto}>{proyecto}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="filtro-rol">Rol</label>
              <select
                id="filtro-rol"
                value={filtroRol}
                onChange={(e) => setFiltroRol(e.target.value)}
              >
                <option value="">Todos</option>
                <option value="empleado">Empleado</option>
                <option value="HSEQ">HSEQ</option>
                <option value="admin">Administrador</option>
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="filtro-estado">Estado</label>
              <select
                id="filtro-estado"
                value={filtroEstado}
                onChange={(e) => setFiltroEstado(e.target.value)}
              >
                <option value="">Todos</option>
                <option value="activo">Activo</option>
                <option value="retirado">Retirado</option>
              </select>
            </div>

            {hayFiltrosActivos && (
              <button
                type="button"
                className="clear-filters-button"
                onClick={limpiarFiltros}
              >
                Limpiar
              </button>
            )}
          </div>

          <p className="filters-count">
            Mostrando {empleadosFiltrados.length} de {empleados.length} empleados
          </p>
        </div>

        {showForm && (
          <div className="crud-form-container">
            <form
              className="crud-form"
              onSubmit={handleSubmit}
            >
              <h2>{isEditing ? 'Editar Empleado' : 'Nuevo Empleado'}</h2>

              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="cedula">Cédula:</label>
                  <input
                    type="text"
                    id="cedula"
                    name="cedula"
                    value={currentEmpleado.cedula}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="tipo_documento">Tipo de Documento:</label>
                  <select
                    id="tipo_documento"
                    name="tipo_documento"
                    value={currentEmpleado.tipo_documento}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="Cédula de Ciudadanía">Cédula de Ciudadanía</option>
                    <option value="Cédula de extranjeria">Cédula de extranjería</option>
                    <option value="CC">CC</option>
                    <option value="PPT">Permiso por Protección Temporal</option>
                  </select>
                </div>

                <div className="form-group form-group-full">
                  <label htmlFor="nombre">Nombre completo:</label>
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    value={currentEmpleado.nombre}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="cargo">Cargo:</label>
                  <select
                    id="cargo"
                    name="cargo"
                    value={currentEmpleado.cargo}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Seleccionar cargo...</option>
                    {cargos.length > 0 ? (
                      cargos.map(cargo => (
                        <option key={cargo.id_cargo} value={cargo.nombre_cargo}>
                          {cargo.nombre_cargo}
                        </option>
                      ))
                    ) : (
                      <option disabled>Cargando cargos...</option>
                    )}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="area">Área:</label>
                  <input
                    list="lista-areas"
                    type="text"
                    id="area"
                    name="area"
                    value={currentEmpleado.area || ''}
                    onChange={handleInputChange}
                    required
                  />
                  <datalist id="lista-areas">
                    {areasFormulario.map(area => (
                      <option key={area} value={area} />
                    ))}
                  </datalist>
                </div>

                <div className="form-group">
                  <label htmlFor="proyecto">Proyecto:</label>
                  <input
                    list="lista-proyectos"
                    type="text"
                    id="proyecto"
                    name="proyecto"
                    value={currentEmpleado.proyecto || ''}
                    onChange={handleInputChange}
                    placeholder="Ej: ADMINISTRACION, FRONTERA..."
                  />
                  <datalist id="lista-proyectos">
                    {proyectosDisponibles.map(proyecto => (
                      <option key={proyecto} value={proyecto} />
                    ))}
                  </datalist>
                </div>

                <div className="form-group">
                  <label htmlFor="fecha_inicio_contrato">Fecha de Inicio de Contrato:</label>
                  <input
                    type="date"
                    id="fecha_inicio_contrato"
                    name="fecha_inicio_contrato"
                    value={currentEmpleado.fecha_inicio_contrato || ''}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Correo electrónico:</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={currentEmpleado.email || ''}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contrasena">
                    Contraseña:{isEditing ? ' (dejar vacío para no cambiar)' : ''}
                  </label>
                  <input
                    type="password"
                    id="contrasena"
                    name="contrasena"
                    value={currentEmpleado.contrasena || ''}
                    onChange={handleInputChange}
                    required={!isEditing}
                    placeholder={isEditing ? 'Sin cambios' : ''}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="ods">ODS:</label>
                  <input
                    type="text"
                    id="ods"
                    name="ods"
                    value={currentEmpleado.ods || ''}
                    onChange={handleInputChange}
                    placeholder="Número de ODS si aplica"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="rol">Rol:</label>
                  <select
                    id="rol"
                    name="rol"
                    value={currentEmpleado.rol || 'empleado'}
                    onChange={handleInputChange}
                  >
                    <option value="empleado">Empleado</option>
                    <option value="HSEQ">HSEQ</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="estado">Estado:</label>
                  <select
                    id="estado"
                    name="estado"
                    value={currentEmpleado.estado || 'activo'}
                    onChange={handleInputChange}
                  >
                    <option value="activo">Activo</option>
                    <option value="retirado">Retirado</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="tipo_poblacion">Tipo de población:</label>
                  <select
                    id="tipo_poblacion"
                    name="tipo_poblacion"
                    value={currentEmpleado.tipo_poblacion || ''}
                    onChange={handleInputChange}
                  >
                    <option value="">Seleccionar...</option>
                    <option value="Administrativo">Administrativo</option>
                    <option value="Operación/Campo">Operación/Campo</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="nivel_organizacional">Nivel organizacional:</label>
                  <select
                    id="nivel_organizacional"
                    name="nivel_organizacional"
                    value={currentEmpleado.nivel_organizacional || ''}
                    onChange={handleInputChange}
                  >
                    <option value="">Seleccionar...</option>
                    <option value="Estratégico">Estratégico</option>
                    <option value="Táctico">Táctico</option>
                    <option value="Técnico">Técnico</option>
                    <option value="Operativo administrativo">Operativo administrativo</option>
                    <option value="Operación/Campo">Operación/Campo</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="config_evaluacion">Config. evaluación:</label>
                  <select
                    id="config_evaluacion"
                    name="config_evaluacion"
                    value={currentEmpleado.config_evaluacion || ''}
                    onChange={handleInputChange}
                  >
                    <option value="">Seleccionar...</option>
                    <option value="EST">EST</option>
                    <option value="TAC">TAC</option>
                    <option value="TEC">TEC</option>
                    <option value="TEC-L">TEC-L</option>
                    <option value="OPA">OPA</option>
                    <option value="OP-CAMPO">OP-CAMPO</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="modalidad_evaluacion">Modalidad evaluación:</label>
                  <select
                    id="modalidad_evaluacion"
                    name="modalidad_evaluacion"
                    value={currentEmpleado.modalidad_evaluacion || ''}
                    onChange={handleInputChange}
                  >
                    <option value="">Seleccionar...</option>
                    <option value="180°">180°</option>
                    <option value="360°">360°</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="autoevaluacion">Autoevaluación:</label>
                  <select
                    id="autoevaluacion"
                    name="autoevaluacion"
                    value={currentEmpleado.autoevaluacion || ''}
                    onChange={handleInputChange}
                  >
                    <option value="">Seleccionar...</option>
                    <option value="Sí">Sí</option>
                    <option value="No">No</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="jefe_inmediato">Jefe inmediato:</label>
                  <input
                    list="lista-jefes"
                    type="text"
                    id="jefe_inmediato"
                    name="jefe_inmediato"
                    value={currentEmpleado.jefe_inmediato || ''}
                    onChange={handleInputChange}
                    placeholder="Nombre del jefe"
                  />
                  <datalist id="lista-jefes">
                    {jefesDisponibles.map(nombre => (
                      <option key={nombre} value={nombre} />
                    ))}
                  </datalist>
                </div>

                <div className="form-group form-group-full">
                  <label htmlFor="ponderacion_aplicable">Ponderación aplicable:</label>
                  <input
                    type="text"
                    id="ponderacion_aplicable"
                    name="ponderacion_aplicable"
                    value={currentEmpleado.ponderacion_aplicable || ''}
                    onChange={handleInputChange}
                    placeholder="Ej: 45% Jefe / 25% Colab. / 20% Pares / 10% Auto"
                  />
                </div>

                <div className="form-group form-group-checkbox">
                  <label htmlFor="tiene_personal_a_cargo">
                    <input
                      type="checkbox"
                      id="tiene_personal_a_cargo"
                      name="tiene_personal_a_cargo"
                      checked={Boolean(Number(currentEmpleado.tiene_personal_a_cargo))}
                      onChange={handleInputChange}
                    />
                    Tiene personal a cargo
                  </label>
                </div>

                <div className="form-group form-group-full">
                  <label htmlFor="observaciones_gh">Observaciones GH:</label>
                  <textarea
                    id="observaciones_gh"
                    name="observaciones_gh"
                    rows="3"
                    value={currentEmpleado.observaciones_gh || ''}
                    onChange={handleInputChange}
                    placeholder="Notas de Gestión Humana"
                  />
                </div>
              </div>

              <div className="form-buttons">
                <button type="button" className="cancel-button" onClick={handleCancelForm}>
                  Cancelar
                </button>
                <button type="submit" className="save-button">
                  {isEditing ? 'Actualizar' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="crud-table-container">
          {(empleadosFiltrados || []).length === 0 ? (
            <p className="no-data">
              {hayFiltrosActivos
                ? 'No hay empleados que coincidan con los filtros'
                : 'No hay empleados registrados'}
            </p>
          ) : (
            <table className="crud-table">
              <thead>
                <tr>
                  <th>Cédula</th>
                  <th>Nombre</th>
                  <th>Cargo</th>
                  <th>Área</th>
                  <th>Proyecto</th>
                  <th>Correo</th>
                  <th>Rol</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {empleadosFiltrados.map(empleado => (
                  <tr key={empleado.id_empleado}>
                    <td>{empleado.cedula}</td>
                    <td>{empleado.nombre}</td>
                    <td>{empleado.cargo}</td>
                    <td>{empleado.area}</td>
                    <td>{empleado.proyecto || 'N/A'}</td>
                    <td>{empleado.email}</td>
                    <td>
                      {empleado.rol === 'admin' ? 'Administrador' :
                       empleado.rol === 'HSEQ' ? 'HSEQ' : 'Empleado'}
                    </td>
                    <td>
                      <span className={`estado-badge ${empleado.estado || 'activo'}`}>
                        {(empleado.estado || 'activo') === 'retirado' ? 'Retirado' : 'Activo'}
                      </span>
                    </td>
                    <td className="acciones">
                      <div className="acciones-inner">
                        <button
                          className="edit-button"
                          title="Descargar PDF de la última evaluación"
                          disabled={pdfGenerandoId !== null}
                          onClick={() => descargarPdfEmpleado(empleado)}
                        >
                          <Icon
                            name={pdfGenerandoId === empleado.id_empleado ? 'spinner' : 'document'}
                            size={16}
                            title="Descargar PDF"
                          />
                        </button>
                        <button
                          className="edit-button" 
                          onClick={() => handleEditClick(empleado)}
                        >
                          <Icon name="edit" size={16} title="Editar" />
                        </button>
                        <button 
                          className="delete-button" 
                          onClick={() => handleDeleteClick(empleado.id_empleado)}
                        >
                          <Icon name="trash" size={16} title="Eliminar" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>

      <Footer />
      </div>
    </>
  );
}

export default EmpleadosCRUD; 